import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { ref } from 'vue';
import { isAxiosError } from 'axios';
import { AW_AX_CACHE_SEGMENTATION } from '~~/common/plugins/axios/awAxCache';
import type { DeepPartial, Schema } from '~~/common/types/api';
import { useAwPerf } from '~~/common/composables/useAwPerf';

type CmsContent = DeepPartial<Schema<'ContentVersionModel'>>;
// `awCmsContentHref` is a FE-only field appended to modal content; not part of the schema.
type CmsModalContent = CmsContent & { awCmsContentHref?: string };
// `_awMeta` is a FE-only cache marker appended to preview content; not part of the schema.
type CmsContentPreview = CmsContent & { _awMeta?: { cacheKey: string } };

export const useContentsStore = defineStore('contents', () => {
  const nuxtApp = useNuxtApp();
  const awPerf = useAwPerf();

  const data = ref<CmsContent>({});
  const staticContent = ref<Record<string, CmsContent>>({});
  const staticContentUsers = ref<Record<string, number>>({});
  const previewData = ref<CmsContentPreview>({});
  const dataByResourceId = ref<CmsContent[]>([]);
  const needPetrolContent = ref(false);
  const modalContent = ref<CmsModalContent>({});

  function clearDataByResourceId () {
    dataByResourceId.value = [];
  }

  async function fetchContent ({
    pageId,
  }: {
    pageId: string | number;
  }) {
    const { $api, $logger } = nuxtApp;
    try {
      data.value = await $api.$get<CmsContent>(`/cms/content/${pageId}`, { awAxCache: AW_AX_CACHE_SEGMENTATION });
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
    }
  }

  async function fetchModalContent ({
    pageId,
    awCmsContentHref,
  }: {
    pageId: string | number;
    awCmsContentHref: string;
  }) {
    const { $api, $logger } = nuxtApp;
    try {
      const cmsData = await $api.$get<CmsModalContent>(`/cms/content/${pageId}`, { awAxCache: AW_AX_CACHE_SEGMENTATION });
      if (cmsData && awCmsContentHref) {
        cmsData.awCmsContentHref = awCmsContentHref;
      }
      modalContent.value = cmsData;
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
    }
  }

  function increaseStaticContentUsers ({
    type,
  }: {
    type: string;
  }) {
    const current = staticContentUsers.value[type];
    staticContentUsers.value[type] = typeof current === 'number' ? current + 1 : 1;
  }

  function decreaseStaticContentUsers ({
    type,
  }: {
    type: string;
  }) {
    const current = staticContentUsers.value[type];
    if (typeof current === 'number' && current > 0) {
      staticContentUsers.value[type] = current - 1;
    } else {
      staticContentUsers.value[type] = 0;
    }
    const updated = staticContentUsers.value[type];
    if (typeof updated !== 'number' || updated < 1) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete staticContent.value[type];
    }
  }

  async function fetchStaticContent ({
    type,
  }: {
    type: string;
  }) {
    const { $api, $logger } = nuxtApp;

    try {
      awPerf?.markPhase('staticContentPagesStart');
      const cached = staticContent.value[type];
      if (cached?.id) {
        data.value = cached;
      } else {
        data.value = await $api.$get<CmsContent>(`/static_content_pages/${type}`, { awAxCache: AW_AX_CACHE_SEGMENTATION });
      }

      if (type === 'homepage' || type === 'dh_homepage') {
        staticContent.value[type] = data.value;
      }
      awPerf?.markPhase('staticContentPagesDone');
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
    }
  }

  async function fetchStaticContentByResourceId ({
    type,
    resourceId,
  }: {
    type: string;
    resourceId: string | number;
  }) {
    const { $api, $logger } = nuxtApp;
    try {
      dataByResourceId.value = await $api.$get<CmsContent[]>(`/connected_content_pages/${type}/${resourceId}`, { awAxCache: AW_AX_CACHE_SEGMENTATION });
    } catch (error) {
      dataByResourceId.value = [];
      if (!isAxiosError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
    }
  }

  async function fetchContentPreview ({
    hash,
  }: {
    hash: string;
  }) {
    const { $api } = nuxtApp;
    if (previewData.value._awMeta?.cacheKey === hash) {
      return;
    }
    const cacheKey = `/cms/content_preview/${hash}`;
    const response = await $api.$get<CmsContent>(cacheKey);
    previewData.value = { ...response, _awMeta: { cacheKey } };
  }

  function setNeedPetrolContent (val: boolean) {
    needPetrolContent.value = val;
  }

  return {
    data,
    staticContent,
    staticContentUsers,
    increaseStaticContentUsers,
    decreaseStaticContentUsers,
    previewData,
    dataByResourceId,
    clearDataByResourceId,
    fetchContent,
    fetchStaticContent,
    fetchStaticContentByResourceId,
    fetchContentPreview,
    needPetrolContent,
    setNeedPetrolContent,
    modalContent,
    fetchModalContent,
  };
});
